import type { PausedRecoveryMetadata } from '../hooks/useGenerationLifecycle';
import type { RecoverySafeResumePoint } from './recoveryCheckpoint.service';

export type InspectorFailureCategory =
  | 'RECOVERABLE_INSPECTOR_FAILURE'
  | 'TARGETED_REPAIR_FAILURE'
  | 'REPAIRABLE_INSPECTOR_BLOCK'
  | 'TERMINAL_CANON_BLOCK';

export type RecoveryActionDescriptor = {
  label: string;
  safeResumePoint: RecoverySafeResumePoint | PausedRecoveryMetadata['safeResumePoint'];
  allowed: boolean;
  explanation: string;
  secondaryActions: Array<'review_block' | 'discard_draft' | 'return_to_project' | 'regenerate_chapter'>;
};

const INSPECTOR_RETRY_CATEGORIES = new Set<InspectorFailureCategory>([
  'RECOVERABLE_INSPECTOR_FAILURE',
  'TARGETED_REPAIR_FAILURE',
  'REPAIRABLE_INSPECTOR_BLOCK',
]);

export function isInspectorFailureCategory(value: string | undefined): value is InspectorFailureCategory {
  return (
    value === 'RECOVERABLE_INSPECTOR_FAILURE' ||
    value === 'TARGETED_REPAIR_FAILURE' ||
    value === 'REPAIRABLE_INSPECTOR_BLOCK' ||
    value === 'TERMINAL_CANON_BLOCK'
  );
}

export function allowsInspectorRetryValidation(category: InspectorFailureCategory | undefined): boolean {
  return !!category && INSPECTOR_RETRY_CATEGORIES.has(category);
}

export function mapInspectorTerminalReasonToFailureCategory(
  terminalReason: string,
  decisionRoute?: string,
): InspectorFailureCategory {
  if (terminalReason === 'recoverable_inspector_failure' || decisionRoute === 'RECOVERABLE_INSPECTOR_FAILURE') {
    return 'RECOVERABLE_INSPECTOR_FAILURE';
  }
  if (terminalReason === 'targeted_repair_failure' || decisionRoute === 'TARGETED_REPAIR') {
    return 'TARGETED_REPAIR_FAILURE';
  }
  if (terminalReason === 'inspector_terminal_canon_block' || decisionRoute === 'TERMINAL_CANON_BLOCK') {
    return 'TERMINAL_CANON_BLOCK';
  }
  if (terminalReason === 'inspector_terminal_block') {
    return 'REPAIRABLE_INSPECTOR_BLOCK';
  }
  return 'REPAIRABLE_INSPECTOR_BLOCK';
}

export function resolveRecoveryAction(metadata: PausedRecoveryMetadata): RecoveryActionDescriptor {
  const resumePoint = metadata.safeResumePoint;

  if (resumePoint === 'INSPECTOR_VALIDATION') {
    const category = isInspectorFailureCategory(metadata.failureCategory)
      ? metadata.failureCategory
      : mapInspectorTerminalReasonToFailureCategory(metadata.errorType || '');
    const retryAllowed = allowsInspectorRetryValidation(category);
    return {
      label: retryAllowed ? 'Retry Validation' : 'Review Block',
      safeResumePoint: 'INSPECTOR_VALIDATION',
      allowed: retryAllowed,
      explanation: retryAllowed
        ? 'Resume Inspector validation from the preserved draft without rerunning Scribe.'
        : 'This draft has a confirmed canon block. Review or discard before retrying validation.',
      secondaryActions: retryAllowed
        ? ['discard_draft', 'return_to_project']
        : ['review_block', 'discard_draft', 'return_to_project'],
    };
  }

  if (resumePoint === 'PHYSICAL_CONTINUITY_EXTRACTION') {
    return {
      label: 'Retry Story Memory',
      safeResumePoint: 'PHYSICAL_CONTINUITY_EXTRACTION',
      allowed: true,
      explanation: 'Resume physical continuity extraction without rerunning Scribe, Inspector, Editor, or Publication Editor.',
      secondaryActions: ['discard_draft', 'return_to_project'],
    };
  }

  if (resumePoint === 'PublicationEditor') {
    return {
      label: 'Resume Publication Editor',
      safeResumePoint: 'PublicationEditor',
      allowed: true,
      explanation: 'Continue from the Publication Editor checkpoint without restarting earlier completed stages.',
      secondaryActions: ['discard_draft', 'return_to_project'],
    };
  }

  if (resumePoint === 'Archivist' || resumePoint === 'SaveChapter') {
    return {
      label: resumePoint === 'Archivist' ? 'Resume Archivist' : 'Resume Save Chapter',
      safeResumePoint: resumePoint,
      allowed: true,
      explanation: 'Continue from the saved recovery checkpoint without rerunning earlier completed stages.',
      secondaryActions: ['discard_draft', 'return_to_project'],
    };
  }

  return {
    label: `Resume from ${resumePoint}`,
    safeResumePoint: resumePoint,
    allowed: metadata.retryAvailable || metadata.draftAvailable || !!metadata.checkpointStoragePath,
    explanation: 'Resume from the preserved recovery checkpoint.',
    secondaryActions: ['discard_draft', 'return_to_project'],
  };
}

export function physicalRecoverySkipsInspector(metadata: PausedRecoveryMetadata | null | undefined): boolean {
  return metadata?.safeResumePoint === 'PHYSICAL_CONTINUITY_EXTRACTION';
}

export function inspectorRecoverySkipsScribe(metadata: PausedRecoveryMetadata | null | undefined): boolean {
  return metadata?.safeResumePoint === 'INSPECTOR_VALIDATION';
}
